import { clsx } from "clsx"
import { InputHTMLAttributes, ReactNode, useEffect, useState } from "react"
import { KeyCodes } from "src/constants"
import { DropdownIcon } from "src/icons"
import { IOption } from "src/types"

export interface SelectControlProps<T = string, M = object> extends Omit<React.HTMLAttributes<HTMLDivElement>, "onInput"> {
  reference: (ref: HTMLElement) => void
  style?: React.CSSProperties
  className?: string
  label?: string
  error?: string
  leftSection?: React.ReactNode | ((value: IOption<T, M> | string) => React.ReactNode)
  rightSection?: React.ReactNode
  value?: IOption<T, M> | string
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  onInput?: (value: string, e: React.FormEvent<HTMLInputElement>) => void
  onNavigate?: (index: number) => void
  onEnter?: VoidFunction
  formatter?: (value: string) => string
  getOptionLabel?: (option: IOption<T, M>) => string
  customControl?: (props: InputHTMLAttributes<HTMLInputElement>) => ReactNode
  lightTheme?: boolean
  variant?: "default" | "text"
}

export const SelectControl = <T extends string | number = string, M extends object = object>({
  error,
  leftSection,
  rightSection,
  label,
  className,
  reference,
  style,
  onClick,
  value,
  inputProps,
  onNavigate,
  onInput,
  onEnter,
  formatter = (value) => value,
  getOptionLabel,
  customControl,
  ...props
}: SelectControlProps<T, M>) => {
  const [inputValue, setInputValue] = useState("")
  useEffect(() => {
    if (getOptionLabel != null && typeof value === "object") setInputValue(getOptionLabel(value))
    else setInputValue(typeof value === "string" ? value : (value?.label ?? ""))
  }, [value, getOptionLabel])
  return (
    <div ref={reference} style={style}>
      {label != null && <span className="select-custom__label">{label}</span>}
      <div
        {...props}
        className={clsx(`select-custom`, className, {
          "select-custom--light": props.lightTheme,
          "select-custom--text": props.variant === "text",
        })}
        style={{ width: "100%" }}
        onClick={onClick}
      >
        {customControl?.({
          onKeyDown: (e) => {
            if (e.key === KeyCodes.ArrowDown || e.key === KeyCodes.ArrowUp) {
              e.preventDefault()
              const isDown = e.key === KeyCodes.ArrowDown
              onNavigate?.(isDown ? 1 : -1)
            }
            if (e.key === KeyCodes.Enter) {
              e.preventDefault()
              onEnter?.()
            }
          },
        }) || (
          <div className="select-custom__control">
            {typeof leftSection === "function" ? leftSection(value) : leftSection}
            {/* {props.variant === "text" && (
              <span className="select-custom__value-span"> {formatter(String(inputProps?.value ?? inputValue))}</span>
            )} */}
            <input
              {...inputProps}
              value={formatter(String(inputProps?.value ?? inputValue))}
              className={clsx("select-custom__input", inputProps?.className)}
              onChange={(e) => {
                setInputValue(e.target.value)
                inputProps?.onChange?.(e)
              }}
              readOnly={props.variant === "text"}
              onKeyDown={(e) => {
                if (e.key === KeyCodes.ArrowDown || e.key === KeyCodes.ArrowUp) {
                  e.preventDefault()
                  const isDown = e.key === KeyCodes.ArrowDown
                  onNavigate?.(isDown ? 1 : -1)
                }
                if (e.key === KeyCodes.Enter) {
                  e.preventDefault()
                  onEnter?.()
                }
              }}
              onInput={(e) => {
                inputProps?.onInput?.(e)
                onInput?.(e.currentTarget.value, e)
              }}
            />
          </div>
        )}
        {rightSection}
        <div className="select-custom__dropdown-icon">
          <DropdownIcon />
        </div>
      </div>
      {error && <span className="select-custom__error">{error}</span>}
    </div>
  )
}
